import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { Theme } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectComponent from 'webapp/components/form/select';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import HeaderOrQueryParameter from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/header-or-query-parameter';

interface Args {
	form: CustomMetricSetupFormApiMetricBuilder;
	isDisabled: boolean;
	theme: string;
	goNext?: () => void;
}

export default class CustomQuerySetupApiMetricBuilderMethodAndUrlComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked path = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.path = this.args.form.userEnteredPath;
	}

	get httpMethodOptions(): LabelValuePairString[] {
		return this.args.form.httpMethodOptions.map((opt) => {
			return {
				value: opt,
				label: opt,
			};
		});
	}

	get isHttpMethodDisabled(): boolean {
		return this.args.isDisabled || this.httpMethodOptions.length <= 1;
	}

	get hasBaseUrl(): boolean {
		return !!this.args.form.baseUrl;
	}

	get methodTheme(): string {
		return this.args.theme === Theme.DARK ? 'blue' : 'grey';
	}

	get showHelpArticlesSection() {
		return (
			!this.accountService.hideHelpArticles &&
			(this.args.form.helpArticleUrl || this.args.form.apiRefLink)
		);
	}

	@action
	changeHttpMethod(httpMethod: string): void {
		this.args.form.httpMethod = httpMethod;
	}

	@action
	triggerFocusOut(): void {
		let userPath = (this.path || '').trim();

		// check if user pasted some params
		const searchParamsString = userPath.split('?')[1];
		if (searchParamsString) {
			const params = new URLSearchParams(searchParamsString);
			for (const [key, value] of params.entries()) {
				// create or update existing parameter rows
				const existing = this.args.form.queryParameters.find((item) => item.key === key);
				if (existing) {
					existing.value = value;
				} else {
					// first, remove empties
					this.args.form.queryParameters = this.args.form.queryParameters.filter(
						(item) => {
							return !item.isEmpty;
						},
					);
					// add a new param row
					const newHeaderOrQuery = new HeaderOrQueryParameter({ key, value });
					this.args.form.queryParameters = [
						...this.args.form.queryParameters,
						newHeaderOrQuery,
					];
				}
			}
			this.infolayerService.ok(
				'Entered query parameters were moved from URL to "Parameters" section.',
			);
			userPath = userPath.split('?')[0]!;
		}

		this.path = userPath;
		this.args.form.userEnteredPath = this.path;
	}

	@action
	setupBaseUrlSize(node: HTMLElement): void {
		const baseUrlElWidth = node.offsetWidth;
		node.style.minWidth = `${baseUrlElWidth}px`;

		const pathInput = node.parentElement?.getElementsByTagName('input')[0];
		if (pathInput) {
			pathInput.style.paddingLeft = `${node.offsetLeft + baseUrlElWidth - 1}px`;
		}
	}

	@action
	handleKeydown(event: KeyboardEvent): void {
		if (!this.args.goNext) return;
		if (event.key === 'Enter') {
			event.preventDefault();
			this.args.goNext();
		}
	}
	<template>
		<div class="row row--method-and-url">

			<FormInputComponent
				name="path"
				@value={{this.path}}
				@focusOut={{this.triggerFocusOut}}
				@theme={{@theme}}
				{{on "keydown" this.handleKeydown}}
			/>

			<div class="item--method">
				<FormSelectComponent
					name="httpMethod"
					@selected={{@form.httpMethod}}
					@options={{this.httpMethodOptions}}
					@onChange={{this.changeHttpMethod}}
					@isNumeric={{false}}
					@isRequired={{true}}
					@isDisabled={{this.isHttpMethodDisabled}}
					@theme={{this.methodTheme}}
				/>
			</div>

			{{#if this.hasBaseUrl}}
				<div class="item--base-url" {{didInsert this.setupBaseUrlSize}}>
					{{@form.baseUrl}}
				</div>
			{{/if}}

			{{#if this.showHelpArticlesSection}}
				<div class="help-articles">
					{{#if @form.helpArticleUrl}}
						<a href={{@form.helpArticleUrl}} target="_blank">
							<CommonUiIconComponent @name="Book" @size="md" @class="pos--before" />
							Help article
						</a>
					{{/if}}
					{{#if @form.apiRefLink}}
						<a href={{@form.apiRefLink}} target="_blank">
							<CommonUiIconComponent @name="Api" @size="md" @class="pos--before" />
							API docs
							<CommonUiIconComponent @name="Share" @size="md" @class="pos--after" />
						</a>
					{{/if}}
				</div>
			{{/if}}

		</div>
	</template>
}
